import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../i18n/LanguageContext';

const PromotionsPopup = ({ promotions = [], onClose }) => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const total = promotions?.length;

  const goNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e?.key === 'ArrowRight') goNext();
      if (e?.key === 'ArrowLeft') goPrev();
      if (e?.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev, onClose]);

  const handleTouchStart = (e) => {
    setTouchStartX(e?.touches?.[0]?.clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (touchStartX === null) return;
    const diff = e?.touches?.[0]?.clientX - touchStartX;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (Math.abs(dragOffset) > 50) {
      if (dragOffset < 0) goNext();
      else goPrev();
    }
    setTouchStartX(null);
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseDown = (e) => {
    setTouchStartX(e?.clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || touchStartX === null) return;
    setDragOffset(e?.clientX - touchStartX);
  };

  const handleMouseUp = () => {
    if (Math.abs(dragOffset) > 50) {
      if (dragOffset < 0) goNext();
      else goPrev();
    }
    setTouchStartX(null);
    setIsDragging(false);
    setDragOffset(0);
  };

  if (!total) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6"
      onClick={onClose}
    >
      {/* Modal window */}
      <div
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: 'min(90vh, 700px)' }}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={(e) => { e?.stopPropagation(); onClose?.(); }}
          className="absolute top-3 right-3 z-30 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          aria-label={t('promoClose')}
        >
          <Icon name="X" size={16} color="white" />
        </button>

        {/* Counter */}
        {total > 1 && (
          <div className="absolute top-3 left-3 z-20 px-2.5 py-0.5 rounded-full bg-black/50">
            <span className="text-xs font-medium text-white">
              {currentIndex + 1} / {total}
            </span>
          </div>
        )}

        {/* Slides track */}
        <div
          className="overflow-hidden cursor-grab active:cursor-grabbing flex-1 min-h-0"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            className="flex h-full"
            style={{
              width: `${total * 100}%`,
              transform: `translateX(${isDragging
                ? `calc(${(-currentIndex / total) * 100}% + ${dragOffset / total}px)`
                : `${(-currentIndex / total) * 100}%`
              })`,
              transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            {promotions?.map((promo, idx) => {
              const hasText = promo?.title || promo?.description;
              return (
              <div
                key={promo?.id || idx}
                className="flex flex-col"
                style={{ width: `${100 / total}%` }}
              >
                {/* Image area */}
                <div
                  className="w-full bg-gray-100 flex items-center justify-center overflow-hidden flex-1 min-h-0"
                  style={{ height: hasText ? '360px' : undefined }}
                >
                  {promo?.imageUrl ? (
                    <img
                      src={promo?.imageUrl}
                      alt={promo?.title || 'Promotion'}
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <Icon name="Image" size={48} color="rgba(0,0,0,0.2)" />
                    </div>
                  )}
                </div>

                {/* Text content */}
                {hasText && (
                  <div className="px-5 py-4 bg-white">
                    {promo?.title && (
                      <h3 className="text-base font-bold text-gray-900 mb-1 leading-snug">
                        {promo?.title}
                      </h3>
                    )}
                    {promo?.description && (
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {promo?.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>

        {/* Prev / Next arrows */}
        {total > 1 && (
          <>
            <button
              onClick={(e) => { e?.stopPropagation(); goPrev(); }}
              className="absolute left-2 top-[110px] -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 transition-colors z-20"
              aria-label={t('promoPrev')}
            >
              <Icon name="ChevronLeft" size={18} color="white" />
            </button>
            <button
              onClick={(e) => { e?.stopPropagation(); goNext(); }}
              className="absolute right-2 top-[110px] -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 transition-colors z-20"
              aria-label={t('promoNext')}
            >
              <Icon name="ChevronRight" size={18} color="white" />
            </button>
          </>
        )}

        {/* Dots indicator */}
        {total > 1 && (
          <div className="flex justify-center gap-1.5 py-3 bg-white flex-shrink-0">
            {promotions?.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`rounded-full transition-all duration-200 ${
                  idx === currentIndex
                    ? 'w-5 h-2 bg-gray-800' :'w-2 h-2 bg-gray-300 hover:bg-gray-500'
                }`}
                aria-label={`Promotion ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionsPopup;
